class FGUpsellCarousel extends HTMLElement {
    // JSON array of product data for getting recommendations
    // Assumed to be a json array of liquid products if no parser is provided
    #context = [];
    // Array of product ids to search for recommendations. NOT the gid://
    #productIds = [];
    // Maximum number of items in the carousel (data-attr)
    maxItems = 0;
    // Scroll bar include. Leave falsy to not include (data-attr)
    #scrollBarType = '';
    // The products after filtering for being displayed in the carousel
    #recommendations = [];
    // The products from the recommendation API
    #recommendationsResponse = [];
    // Flickity instance
    flickity = null;

    constructor() {
        super();
        const jsonContext = JSON.parse(this.dataset.context);
        this.#context = jsonContext;
        this.#productIds = this.getProductIdsForRec(jsonContext);

        this.maxItems = parseInt(this.dataset.maxItems, 10);
        this.#scrollBarType = this.dataset.scrollBarType;
        this.handleStateChanged = this.handleStateChanged.bind(this);
    }

    async connectedCallback() {
        // Listen for state dropdown changes, re filter recommendations, and rebuild the carousel
        document.addEventListener('fgw_state_change', this.handleStateChanged);

        this.initContent();

        const recommendations = [];
        for (let ii = 0; ii < this.#productIds.length; ii++) {
            const productId = this.#productIds[ii];
            const products = await this.fetchRecommendations(productId);
            const uniqueProducts = products.filter((product) => {
                const productExists = recommendations.some((recommendation) => recommendation.id === product.id);
                return !productExists;
            });
            recommendations.push(...uniqueProducts);
        }

        if (recommendations.length === 0) {
            this.onEmptyRecommendations();
            return;
        }

        this.#recommendationsResponse = recommendations;

        this.#recommendations = this.filterRecommendations(recommendations);

        if (this.#recommendations.length === 0) {
            this.onEmptyRecommendations();
        }

        this.updateContent();
    }

    // Shopify theme editor calls connected and disconnected... a lot, when items are dragged around in the UI.
    // We need to clear out the inner html and kill flickity to avoid duped content in theme editor on drag.
    disconnectedCallback() {
        if (this.flickity) {
            this.flickity.destroy();
        }
        document.removeEventListener('fgw_state_change', this.handleStateChanged);
        this.innerHTML = '';
    }

    initContent() {
        const templateConfig = this.getTemplateConfig();
        const containerTpl = document.getElementById(templateConfig.containerTemplateId);
        const containerTplNode = containerTpl.content.cloneNode(true);
        const itemTpl = document.getElementById(templateConfig.itemTemplateId);
        const itemInfoTpl = document.getElementById(templateConfig.itemInfoTemplateId);
        const atcTpl = document.getElementById(templateConfig.atcTemplateId);
        const carouselContainer = containerTplNode.querySelector(templateConfig.carouselSelector);
        for (let ii = 0; ii < this.getIntialDisplayCount(); ii++) {
            const item = itemTpl.content.cloneNode(true);
            const itemInfo = itemInfoTpl.content.cloneNode(true);
            const atc = atcTpl.content.cloneNode(true);
            item.querySelector('[data-item-info-container-slot]').replaceWith(itemInfo);
            item.querySelector('[data-item-atc-slot]').replaceWith(atc);
            this.setInitItem(item);
            carouselContainer.appendChild(item);
        }
        this.appendChild(containerTplNode);
        this.flickity = new Flickity(carouselContainer, this.getFlickityConfig());
    }

    /**
     * @param {Element} item - cloned node from itemTpl.content
     */
    setInitItem(item) {
        item.querySelector('[data-item-title]').textContent = 'Item Name';
        item.querySelector('[data-item-price]').textContent = '$xx.xx';
        item.querySelector('[data-item-compare-price]').textContent = '$yy.yy';
    }

    updateContent() {
        const templateConfig = this.getTemplateConfig();
        const itemTpl = document.getElementById(templateConfig.itemTemplateId);
        const itemInfoTpl = document.getElementById(templateConfig.itemInfoTemplateId);
        const atcTpl = document.getElementById(templateConfig.atcTemplateId);

        let carouselSlides = [];
        if (this.flickity.isActive) {
            carouselSlides = this.flickity.getCellElements();
        } else {
            // If flickity is disabled with watchCSS, get the initial containers the normal way
            carouselSlides = this.querySelectorAll('[data-carousel-item]');
        }

        this.#recommendations.forEach((recommendation, index) => {
            let carouselSlide = carouselSlides[index];
            // More recommendations than we have slides, append new slide
            if (!carouselSlide) {
                carouselSlide = itemTpl.content.cloneNode(true).firstElementChild;
                const itemInfo = itemInfoTpl.content.cloneNode(true);
                const atc = atcTpl.content.cloneNode(true);
                carouselSlide.querySelector('[data-item-info-container-slot]').replaceWith(itemInfo);
                carouselSlide.querySelector('[data-item-atc-slot]').replaceWith(atc);
                if (this.flickity.isActive) {
                    this.flickity.append(carouselSlide);
                } else {
                    this.flickity.element.appendChild(carouselSlide);
                }
            }
            this.setPopulatedItem(carouselSlide, recommendation);
            const loadingContainer = carouselSlide.querySelector('[data-loading-container]');
            if (loadingContainer) {
                loadingContainer.classList.add('hide');
            }

            const atcButton = carouselSlide.querySelector('[data-item-atc-btn]');
            if (atcButton) {
                atcButton.setAttribute('data-variant-id', recommendation.variants[0].id);
                atcButton.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.onAddToCart(event);
                });
            }
        });
        if (this.flickity.isActive) {
            // If we are left with stale/empty cells after the update, delete them
            for (let ii = this.#recommendations.length; ii < this.getIntialDisplayCount(); ii++) {
                this.flickity.remove(carouselSlides[ii]);
            }
        }
        this.flickity.reloadCells();
        this.attachFlickityEvents();
    }

    /**
     * @param {Element} carouselSlide - element from flickity.getCellElements()
     * @param {any} recommendation - A product returned from the Shopify recommendations api
     */
    setPopulatedItem(carouselSlide, recommendation) {
        carouselSlide.querySelector('[data-item-image]').setAttribute('src', recommendation.featured_image);
        carouselSlide.querySelector('[data-item-title]').textContent = recommendation.title;
        const priceEl = carouselSlide.querySelector('[data-item-price]');
        const comparePriceEl = carouselSlide.querySelector('[data-item-compare-price]');
        let price = this.dollars(recommendation.price);
        priceEl.textContent = `$${price}`;
        priceEl.setAttribute('data-sl-attribute-p', `${recommendation.id}`);
        comparePriceEl.textContent = `$${this.dollars(
            recommendation.compare_at_price
        )}`;
        comparePriceEl.setAttribute('data-sl-attribute-cap', `${recommendation.id}`);
    }

    async fetchRecommendations(productId) {
        const params = new URLSearchParams({
            product_id: productId,
            limit: 10,
            intent: 'related',
        });
        const response = await fetch(`${Shopify.routes.root}recommendations/products.json?${params.toString()}`);
        const data = await response.json();
        return data.products;
    }

    async fetchAndRenderSections(configs) {
        const sections = await this.fetchSections(configs);
        this.renderSections(configs, sections);
    }

    async fetchSections(configs) {
        const sections = configs.map(config => config.section);
        const response = await fetch(`${Shopify.routes.root}?sections=${sections.join(',')}`);
        return await response.json();
    }

    renderSections(configs, sections) {
        const domParser = new DOMParser();
        configs.forEach(config => {
            const sectionElement = domParser.parseFromString(sections[config.section], 'text/html').querySelector(config.childSelector);
            document.querySelector(config.replacesSelector).innerHTML = sectionElement.innerHTML;
        });
    }

    dollars(pennies) {
        return (pennies / 100).toFixed(2);
    }

    /**
    * @param {any[]} jsonContext - assumed to be an array of liquid products
    * @returns {number[]}
    */
    getProductIdsForRec(jsonContext) {
        return jsonContext.map((product) => product.id).slice(0, 3);
    }

    /**
     * 
     * @returns {{
     * containerTemplateId: string, 
     * itemTemplateId: string, 
     * atcTemplateId: string,
     * itemInfoTemplateId: string,
     * carouselSelector: string
     * }}
     */
    getTemplateConfig() {
        return {
            containerTemplateId: 'fg-upsell-container-tpl',
            itemTemplateId: 'fg-upsell-item-tpl',
            atcTemplateId: 'fg-upsell-atc-tpl',
            itemInfoTemplateId: 'fg-upsell-item-info-tpl',
            carouselSelector: '.fg-upsell-carousel-container',
        }
    }

    /**
     * @returns {number} - Number of carousel cells to start with before data is loaded
     */
    getIntialDisplayCount() {
        return 1;
    }

    /**
     * 
     * @returns {any} - Shopify line item properties added to each item during cart/add 
     */
    getLineItemProperties() {
        return {
            '_fg_widget_id': this.tagName
        }
    }

    handleStateChanged(event) {
        setCurrentWarehouse();
        this.#recommendations = this.filterRecommendations(this.#recommendationsResponse);
        this.updateContent();
    }

    /**
     * @param {any[]} recommendations - array of recommendations from the recommendations api
     * @returns {any[]} - filtered array of recommendations
     */
    filterRecommendations(recommendations) {
        if (!fg_wh) {
            setCurrentWarehouse();
        }
        const recs = recommendations.filter(
            (recommendation) =>
                recommendation.type === 'Bottle' &&
                recommendation.requires_selling_plan === false &&
                recommendation.available === true &&
                !recommendation.tags.includes(`sold-out-${fg_wh}`) &&
                !recommendation.tags.includes(`sold-out-all`)
        );
        this.shuffleArray(recs);
        return recs.slice(0, this.maxItems);
    }

    shuffleArray(array) {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
    }

    onEmptyRecommendations() {
    }

    attachFlickityEvents() {
        const scrollbarContainer = this.querySelector('.fg-upsell-carousel-scrollbar');

        if (!this.#scrollBarType) {
            scrollbarContainer.classList.add('hidden');
            return;
        }
        scrollbarContainer.classList.remove('hidden');

        const scrollbarTrack = scrollbarContainer.querySelector('.track');
        const scrollbarShuttle = scrollbarTrack.querySelector('.shuttle');

        const itemCount = this.flickity.slides.length;
        const shuttleWidthPercent = 100 / itemCount;
        scrollbarShuttle.style.width = `${shuttleWidthPercent}%`;

        const positionType = (progress) => {
            const trackWidth = scrollbarTrack.getBoundingClientRect().width;
            const shuttleWidth = scrollbarShuttle.getBoundingClientRect().width;
            const trackDistance = trackWidth - shuttleWidth;
            let translate = progress * trackDistance;
            if (translate > (trackDistance + (.5 * shuttleWidth))) {
                translate = trackDistance + (.5 * shuttleWidth);
            }
            if (translate < (0 - (.5 * shuttleWidth))) {
                translate = 0 - (.5 * shuttleWidth);
            }
            scrollbarShuttle.style.transform = `translateX(${translate}px)`;
        }

        const progressType = (progress) => {
            scrollbarShuttle.style.transformOrigin = 'bottom left';
            const progressPercent = progress * 100;
            const trackPercent = 100 - shuttleWidthPercent;
            // Since we start our display at the shuttle width, forward progress needs to
            // grow at a rate of trackPercent of the flickity progress.
            // Ex. If the shuttle width is 16%, then the growth rate is 84% of flickity progress
            let newWidth = (progressPercent * (trackPercent / 100)) + shuttleWidthPercent;
            if (progressPercent > 100) {
                newWidth = 100;
            }
            // Allow some bounce on the negative scroll
            if (newWidth < shuttleWidthPercent / 10) {
                newWidth = shuttleWidthPercent / 10;
            }
            scrollbarShuttle.style.width = `${newWidth}%`;
        }

        let scrollDebounce = null;
        this.flickity.on('scroll', progress => {
            clearTimeout(scrollDebounce);
            scrollDebounce = setTimeout(() => {
                if (this.#scrollBarType === 'position') {
                    positionType(progress);
                }
                if (this.#scrollBarType === 'progress') {
                    progressType(progress);
                }
            }, 3);
        });
    }

    /**
     * @returns {any} - flickity config
     */
    getFlickityConfig() {
        return {
            cellAlign: 'left',
            contain: true,
        };
    }

    async delay(ms) {
        return new Promise((res, rej) => setTimeout(() => res(null), ms));
    }

    /**
     * @param {Event} event - Add to cart button click event
     * @return {Response} - The fetch response from shopify add to cart api
     */
    async onAddToCart(event, sectionRenderConfig = []) {
        const element = event.currentTarget;
        element.classList.add('loading');
        element.disabled = true;
        const variantId = JSON.parse(element.dataset.variantId);
        let items = [];
        if (Array.isArray(variantId)) {
            items = variantId.map(id => {
                return {
                    id: id,
                    quantity: 1,
                    properties: this.getLineItemProperties()
                }
            })
        } else {
            items.push({
                id: variantId,
                quantity: 1,
                properties: this.getLineItemProperties()
            })
        }
        const data = {
            items: items,
            sections: sectionRenderConfig ? sectionRenderConfig.map(config => config.section).join(',') : ''
        }

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        const result = await response.json();
        element.classList.remove('loading');
        element.disabled = false;
        this.renderSections(sectionRenderConfig, result.sections);
        return result;
    }
}
customElements.define('fg-upsell-carousel', FGUpsellCarousel);